import { useState, useEffect } from "react";
import { auth } from "./firebase";
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, sendPasswordResetEmail } from "firebase/auth";
import { createUserDoc, getUserDoc, addMembershipToUser, createCompanyDoc, getCompanyDoc, addCompanyMember, getInviteDoc, markInviteUsed } from "./firestore";
import { APP_PUBLIC_URL } from "./helpers";

// ---- STYLES ----
var pageS = { minHeight: "100vh", background: "linear-gradient(135deg,#0f172a 0%,#1e293b 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans','Segoe UI',sans-serif" };
var cardS = { background: "#fff", borderRadius: 16, padding: "40px 36px", width: 400, maxWidth: "90vw", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" };
var inputS = { width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, marginBottom: 12, boxSizing: "border-box" };
var btnS = { width: "100%", padding: "11px 0", borderRadius: 8, border: "none", fontSize: 14, fontWeight: 700, cursor: "pointer", marginBottom: 8 };
var primaryBtn = { ...btnS, background: "linear-gradient(135deg,#2563eb,#7c3aed)", color: "#fff" };
var ghostBtn = { ...btnS, background: "transparent", color: "#6366f1", border: "1px solid #e2e8f0" };
var errS = { color: "#dc2626", fontSize: 12, marginBottom: 10, fontWeight: 600 };
var logoS = { textAlign: "center", marginBottom: 24 };
var logoCircle = { width: 48, height: 48, borderRadius: 12, background: "linear-gradient(135deg,#2563eb,#7c3aed)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 8 };

function genId() { return Math.random().toString(36).slice(2, 10); }

// ======== LOGIN PAGE ========
function LoginPage(props) {
  var [email, setEmail] = useState("");
  var [password, setPassword] = useState("");
  var [err, setErr] = useState("");
  var [msg, setMsg] = useState("");
  var [loading, setLoading] = useState(false);

  function handleLogin(e) {
    e.preventDefault();
    setErr(""); setMsg(""); setLoading(true);
    signInWithEmailAndPassword(auth, email, password)
      .then(function () { setLoading(false); })
      .catch(function (ex) { setErr(ex.message.replace("Firebase: ", "")); setLoading(false); });
  }

  function handleReset() {
    if (!email) { setErr("Enter your email first"); return; }
    setErr(""); setMsg(""); setLoading(true);
    sendPasswordResetEmail(auth, email, { url: APP_PUBLIC_URL })
      .then(function () { setMsg("Password reset email sent — check your inbox"); setLoading(false); })
      .catch(function (ex) { setErr(ex.message.replace("Firebase: ", "")); setLoading(false); });
  }

  return <div style={pageS}>
    <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
    <div style={cardS}>
      <div style={logoS}>
        <div style={logoCircle}><span style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>RD</span></div>
        <div style={{ fontWeight: 700, fontSize: 22, color: "#0f172a" }}>Roster Dr</div>
        <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>Sign in to your account</div>
      </div>
      {err && <div style={errS}>{err}</div>}
      {msg && <div style={{ color: "#059669", fontSize: 12, marginBottom: 10, fontWeight: 600 }}>{msg}</div>}
      <form onSubmit={handleLogin}>
        <input style={inputS} type="email" placeholder="Email" value={email} onChange={function (e) { setEmail(e.target.value); }} required />
        <input style={inputS} type="password" placeholder="Password" value={password} onChange={function (e) { setPassword(e.target.value); }} required />
        <button style={primaryBtn} type="submit" disabled={loading}>{loading ? "Signing in..." : "Sign In"}</button>
      </form>
      <div style={{ textAlign: "center", marginBottom: 8 }}>
        <span onClick={handleReset} style={{ color: "#6366f1", fontSize: 13, cursor: "pointer" }}>Forgot password?</span>
      </div>
      <button style={ghostBtn} onClick={function () { props.onGoSignup(); }}>Create an account</button>
    </div>
  </div>;
}

// ======== SIGNUP PAGE ========
function SignupPage(props) {
  var [mode, setMode] = useState(null); // null | "company" | "invite"
  var [email, setEmail] = useState("");
  var [password, setPassword] = useState("");
  var [displayName, setDisplayName] = useState("");
  var [companyName, setCompanyName] = useState("");
  var [inviteCode, setInviteCode] = useState("");
  var [err, setErr] = useState("");
  var [loading, setLoading] = useState(false);

  function handleCreateCompany(e) {
    e.preventDefault();
    setErr(""); setLoading(true);
    createUserWithEmailAndPassword(auth, email, password)
      .then(function (cred) {
        var uid = cred.user.uid;
        var companyId = genId();
        var membership = { companyId: companyId, role: "admin", providerId: null };
        return createCompanyDoc(companyId, { name: companyName, createdBy: uid })
          .then(function () { return addCompanyMember(companyId, uid, "admin", null, null, null, email); })
          .then(function () { return createUserDoc(uid, { email: email, displayName: displayName, memberships: [membership] }); });
      })
      .then(function () { setLoading(false); })
      .catch(function (ex) { setErr(ex.message.replace("Firebase: ", "")); setLoading(false); });
  }

  function handleJoinInvite(e) {
    e.preventDefault();
    setErr(""); setLoading(true);
    var code = inviteCode.trim();
    getInviteDoc(code)
      .then(function (invite) {
        if (!invite) throw new Error("Invalid invite code");
        if (invite.used) throw new Error("This invite has already been used");
        if (invite.email && invite.email.toLowerCase() !== email.toLowerCase()) throw new Error("This invite is for a different email address");
        return createUserWithEmailAndPassword(auth, email, password)
          .then(function (cred) {
            var uid = cred.user.uid;
            var membership = { companyId: invite.companyId, role: invite.role, providerId: invite.providerId };
            return createUserDoc(uid, { email: email, displayName: displayName, memberships: [membership] })
              .then(function () { return addCompanyMember(invite.companyId, uid, invite.role, invite.providerId, code, invite.permissions || null, email); })
              .then(function () { return markInviteUsed(code); });
          });
      })
      .then(function () { setLoading(false); })
      .catch(function (ex) { setErr(ex.message.replace("Firebase: ", "")); setLoading(false); });
  }

  if (!mode) {
    return <div style={pageS}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <div style={cardS}>
        <div style={logoS}>
          <div style={logoCircle}><span style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>RD</span></div>
          <div style={{ fontWeight: 700, fontSize: 22, color: "#0f172a" }}>Get Started</div>
          <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>How would you like to join?</div>
        </div>
        <button style={{ ...primaryBtn, marginBottom: 12 }} onClick={function () { setMode("company"); }}>Create a New Company</button>
        <button style={ghostBtn} onClick={function () { setMode("invite"); }}>Join with Invite Code</button>
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <span style={{ color: "#64748b", fontSize: 13 }}>Already have an account? </span>
          <span onClick={function () { props.onGoLogin(); }} style={{ color: "#6366f1", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Sign in</span>
        </div>
      </div>
    </div>;
  }

  return <div style={pageS}>
    <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
    <div style={cardS}>
      <div style={logoS}>
        <div style={logoCircle}><span style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>RD</span></div>
        <div style={{ fontWeight: 700, fontSize: 22, color: "#0f172a" }}>{mode === "company" ? "Create Company" : "Join Your Team"}</div>
        {mode === "invite" && <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>Your admin sent you an invite code via email or text</div>}
      </div>
      {err && <div style={errS}>{err}</div>}
      <form onSubmit={mode === "company" ? handleCreateCompany : handleJoinInvite}>
        <input style={inputS} placeholder="Your Full Name" value={displayName} onChange={function (e) { setDisplayName(e.target.value); }} required />
        <input style={inputS} type="email" placeholder="Email Address" value={email} onChange={function (e) { setEmail(e.target.value); }} required />
        <input style={inputS} type="password" placeholder="Create a Password (min 6 characters)" value={password} onChange={function (e) { setPassword(e.target.value); }} required minLength={6} />
        {mode === "company" && <input style={inputS} placeholder="Company Name" value={companyName} onChange={function (e) { setCompanyName(e.target.value); }} required />}
        {mode === "invite" && <div>
          <input style={inputS} placeholder="Paste your invite code here (e.g., AB12CD34)" value={inviteCode} onChange={function (e) { setInviteCode(e.target.value.toUpperCase()); }} required />
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: -8, marginBottom: 12 }}>Check your email or text messages for the code from your admin</div>
        </div>}
        <button style={primaryBtn} type="submit" disabled={loading}>{loading ? "Creating account..." : (mode === "company" ? "Create Company & Sign Up" : "Join & Sign Up")}</button>
      </form>
      <button style={ghostBtn} onClick={function () { setMode(null); setErr(""); }}>Back</button>
    </div>
  </div>;
}

// ======== COMPANY PICKER ========
function CompanyPicker(props) {
  var [joinCode, setJoinCode] = useState("");
  var [showJoin, setShowJoin] = useState(false);
  var [err, setErr] = useState("");
  var [loading, setLoading] = useState(false);

  function handleJoin(e) {
    e.preventDefault();
    setErr(""); setLoading(true);
    var code = joinCode.trim();
    getInviteDoc(code)
      .then(function (invite) {
        if (!invite) throw new Error("Invalid invite code");
        if (invite.used) throw new Error("This invite has already been used");
        var membership = { companyId: invite.companyId, role: invite.role, providerId: invite.providerId };
        return addMembershipToUser(props.uid, membership)
          .then(function () { return addCompanyMember(invite.companyId, props.uid, invite.role, invite.providerId, code, invite.permissions || null, (auth.currentUser && auth.currentUser.email) || null); })
          .then(function () { return markInviteUsed(code); })
          .then(function () { return props.onRefreshUser(); });
      })
      .then(function () { setLoading(false); setShowJoin(false); setJoinCode(""); })
      .catch(function (ex) { setErr(ex.message.replace("Firebase: ", "")); setLoading(false); });
  }

  return <div style={pageS}>
    <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
    <div style={cardS}>
      <div style={logoS}>
        <div style={logoCircle}><span style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>RD</span></div>
        <div style={{ fontWeight: 700, fontSize: 22, color: "#0f172a" }}>Select Company</div>
        <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>You belong to multiple organizations</div>
      </div>
      <div style={{ marginBottom: 16 }}>
        {props.memberships.map(function (m, i) {
          return <button key={i} onClick={function () { props.onSelect(m); }} style={{ width: "100%", padding: "14px 16px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#f8fafc", marginBottom: 8, textAlign: "left", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>{m.companyName || m.companyId}</div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Role: {m.role}</div>
            </div>
            <span style={{ color: "#94a3b8", fontSize: 18 }}>→</span>
          </button>;
        })}
      </div>
      {!showJoin && <button style={ghostBtn} onClick={function () { setShowJoin(true); }}>Join Another Company</button>}
      {showJoin && <form onSubmit={handleJoin}>
        {err && <div style={errS}>{err}</div>}
        <input style={inputS} placeholder="Invite Code" value={joinCode} onChange={function (e) { setJoinCode(e.target.value); }} required />
        <button style={primaryBtn} type="submit" disabled={loading}>{loading ? "Joining..." : "Join"}</button>
        <button type="button" style={ghostBtn} onClick={function () { setShowJoin(false); setErr(""); }}>Cancel</button>
      </form>}
      <div style={{ textAlign: "center", marginTop: 12 }}>
        <span onClick={props.onLogout} style={{ color: "#dc2626", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Sign Out</span>
      </div>
    </div>
  </div>;
}

// ======== AUTH GATE ========
export default function AuthGate(props) {
  var [authUser, setAuthUser] = useState(undefined); // undefined = loading, null = logged out
  var [userDoc, setUserDoc] = useState(null);
  var [selectedMembership, setSelectedMembership] = useState(null);
  var [companyName, setCompanyName] = useState("");
  var [page, setPage] = useState("login"); // "login" | "signup"
  var [loadingDoc, setLoadingDoc] = useState(false);

  // Listen for auth state
  useEffect(function () {
    var unsub = onAuthStateChanged(auth, function (user) {
      setAuthUser(user || null);
      if (!user) { setUserDoc(null); setSelectedMembership(null); }
    });
    return unsub;
  }, []);

  // Load user doc when auth user changes (retry if doc not yet written by signup flow)
  useEffect(function () {
    if (!authUser) return;
    var cancelled = false;
    setLoadingDoc(true);
    function tryLoad(retries) {
      getUserDoc(authUser.uid).then(function (doc) {
        if (cancelled) return;
        if (!doc && retries > 0) {
          setTimeout(function () { tryLoad(retries - 1); }, 800);
          return;
        }
        setUserDoc(doc);
        if (doc && doc.memberships && doc.memberships.length === 1) {
          selectMembership(doc.memberships[0]);
        }
        setLoadingDoc(false);
      }).catch(function () { if (!cancelled) setLoadingDoc(false); });
    }
    tryLoad(5);
    return function () { cancelled = true; };
  }, [authUser]);

  function refreshUser() {
    if (!authUser) return Promise.resolve();
    return getUserDoc(authUser.uid).then(function (doc) {
      setUserDoc(doc);
      if (doc && doc.memberships && doc.memberships.length === 1) {
        setSelectedMembership(doc.memberships[0]);
      }
    });
  }

  function handleLogout() {
    signOut(auth);
    setPage("login");
  }

  function handleSwitchCompany() {
    setSelectedMembership(null);
    setCompanyName("");
  }

  function selectMembership(m) {
    setSelectedMembership(m);
    // If CompanyPicker already enriched it with a name, use that
    if (m.companyName) { setCompanyName(m.companyName); return; }
    // Otherwise fetch it
    getCompanyDoc(m.companyId).then(function (doc) {
      setCompanyName(doc ? doc.name : m.companyId);
    });
  }

  // Loading spinner
  if (authUser === undefined || loadingDoc) {
    return <div style={{ ...pageS, flexDirection: "column", gap: 16 }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <div style={logoCircle}><span style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>RD</span></div>
      <div style={{ color: "#94a3b8", fontSize: 14 }}>Loading...</div>
    </div>;
  }

  // Not logged in
  if (!authUser) {
    if (page === "signup") return <SignupPage onGoLogin={function () { setPage("login"); }} />;
    return <LoginPage onGoSignup={function () { setPage("signup"); }} />;
  }

  // Logged in but no user doc yet
  if (!userDoc) {
    return <div style={{ ...pageS, flexDirection: "column", gap: 16 }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <div style={logoCircle}><span style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>RD</span></div>
      <div style={{ color: "#fca5a5", fontSize: 14 }}>Account not found in database.</div>
      <div style={{ display: "flex", gap: 8 }}>
        <button style={{ ...ghostBtn, width: "auto", padding: "8px 24px", color: "#fff", borderColor: "rgba(255,255,255,0.3)" }} onClick={function () { window.location.reload(); }}>Retry</button>
        <button style={{ ...ghostBtn, width: "auto", padding: "8px 24px", color: "#fca5a5", borderColor: "rgba(255,255,255,0.3)" }} onClick={handleLogout}>Sign Out</button>
      </div>
    </div>;
  }

  // Logged in, multiple memberships, no selection yet
  if (!selectedMembership) {
    // Enrich memberships with company names
    return <CompanyPickerLoader
      uid={authUser.uid}
      memberships={userDoc.memberships}
      onSelect={function (m) { selectMembership(m); }}
      onLogout={handleLogout}
      onRefreshUser={refreshUser}
    />;
  }

  // Logged in + company selected — render the app
  return props.children({
    user: authUser,
    userDoc: userDoc,
    companyId: selectedMembership.companyId,
    companyName: companyName || selectedMembership.companyId,
    membership: selectedMembership,
    onLogout: handleLogout,
    onSwitchCompany: userDoc.memberships.length > 1 ? handleSwitchCompany : null
  });
}

// Helper to load company names for the picker
function CompanyPickerLoader(props) {
  var [enriched, setEnriched] = useState(null);

  useEffect(function () {
    Promise.all(props.memberships.map(function (m) {
      return getCompanyDoc(m.companyId).then(function (doc) {
        return { ...m, companyName: doc ? doc.name : m.companyId };
      });
    })).then(function (result) { setEnriched(result); });
  }, [props.memberships]);

  if (!enriched) return <div style={pageS}>
    <div style={{ color: "#94a3b8", fontSize: 14 }}>Loading companies...</div>
  </div>;

  return <CompanyPicker
    uid={props.uid}
    memberships={enriched}
    onSelect={props.onSelect}
    onLogout={props.onLogout}
    onRefreshUser={props.onRefreshUser}
  />;
}
